import { ArrowRight, Star } from "lucide-react";
import { trackEvent } from "./hooks/usePageTracking";
import { Helmet } from "react-helmet";
import { Waitlist } from "./pages/Waitlist";
import { useEffect, useState } from "react";
import { eligibleCountries } from "./utils/countries";

// Using absolute paths for images from public directory
const basedCardImage = "/assets/based-card-horizontal.jpg";
const lockSvg = "/assets/3d-lock.svg";
import { Routes, Route, Link } from "react-router-dom";
// import { Security } from "./pages/Security";
// import { Gold } from "./pages/Gold";
import TermsAndConditions from "./pages/TermsAndConditions";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
// import { FAQ } from "./pages/FAQ";
import { Layout } from "./components/Layout";
import BlogPost from "./components/BlogPost";
import BlogIndex from "./components/BlogIndex";
import CardTerms from "./pages/CardTerms";
// import Hyper from "./pages/Hyper";
// import Cards from "./pages/Cards";
// import Affiliate from "./pages/Affiliate";

// Add Google Fonts import in the head of your HTML (Kalam only, as Helvetica is a system font)
const googleFontsLink = document.createElement("link");
googleFontsLink.href =
  "https://fonts.googleapis.com/css2?family=Kalam:wght@400&display=swap";
googleFontsLink.rel = "stylesheet";
document.head.appendChild(googleFontsLink);

function App() {
  // State for user's country and eligibility
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [isEligible, setIsEligible] = useState<boolean | null>(null); // null initially, true/false after check
  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(true); // Loading state

  // Fetch and log IP address and location on component mount
  useEffect(() => {
    const fetchIpInfo = async (): Promise<void> => {
      setIsLoadingLocation(true);
      try {
        // Use HTTPS for security
        const response = await fetch(
          `https://ipinfo.io/json?token=${import.meta.env.VITE_IPINFO_TOKEN}`
        );
        // Consider adding your ipinfo.io token for higher limits
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const country = data.country; // Assuming ipinfo returns country code e.g., 'SG'
        const countryName = data.country_name || country; // Get full country name if available
        // Check if the fetched country name is in the eligible list
        // Make sure ipinfo provides the full country name or adjust `eligibleCountries` accordingly
        // For now, let's assume ipinfo.io gives the full name like "Singapore"
        setUserCountry(countryName);
        if (countryName && eligibleCountries.includes(countryName as any)) {
          // Type assertion needed if type mismatch
          setIsEligible(true);
        } else {
          setIsEligible(false);
        }
      } catch (error) {
        console.error("Failed to fetch IP info:", error);
        setIsEligible(false); // Assume not eligible on error
        setUserCountry("your location"); // Default text on error
      } finally {
        setIsLoadingLocation(false);
      }
    };

    fetchIpInfo();
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    <Routes>
      {/* <Route path="/" element={<Hyper />} /> */}
      <Route
        path="/home"
        element={
          <Layout>
            <Helmet>
              <title>BasedApp | Buy, Sell & Spend Crypto</title>
              <meta
                name="description"
                content="BasedApp lets you spend crypto from Solana, Arbitrum, and Polygon at 70+ million merchants worldwide with competitive fees. Apply for your crypto Visa card today."
              />
              <link rel="canonical" href="https://basedapp.io/" />
            </Helmet>
            <div className="min-h-screen bg-white">
              {/* Main Content with Reviews and Hero */}
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Product Update Banner */}
                <div className="mt-4 mb-4">
                  <div className="bg-gradient-to-r from-yellow-600 to-orange-700 text-white py-3 px-4 rounded-lg">
                    <div className="flex items-center justify-center text-center">
                      <p className="text-sm md:text-base font-medium">
                        <span className="inline-flex items-center">
                          <span className="text-yellow-300 mr-2">🎉</span>
                          <span>
                            <strong>New:</strong> BasedApp Standard Cards now
                            available in Hong Kong and Malaysia! Sign up now{" "}
                            <a
                              href="https://app.basedapp.io"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-white hover:text-white font-medium transition-colors underline">
                              here
                            </a>
                          </span>
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="pt-8">
                  {/* Top Reviews */}
                  <div className="mb-4">
                    {/* Desktop view - show all reviews */}
                    <div className="hidden md:flex flex-wrap justify-center gap-4">
                      {/* Review 1 */}
                      <div className="text-center">
                        <div className="flex justify-center mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className="w-5 h-5 text-yellow-400 fill-yellow-400"
                            />
                          ))}
                        </div>
                        <p className="text-lg text-gray-800 font-['Kalam'] leading-normal">
                          "Convenient way to off-ramp."
                        </p>
                      </div>

                      {/* Review 2 */}
                      <div className="text-center">
                        <div className="flex justify-center mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className="w-5 h-5 text-yellow-400 fill-yellow-400"
                            />
                          ))}
                        </div>
                        <p className="text-lg text-gray-800 font-['Kalam'] leading-normal">
                          "Spending magic internet money feels different"
                        </p>
                      </div>

                      {/* Review 3 */}
                      <div className="text-center">
                        <div className="flex justify-center mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className="w-5 h-5 text-yellow-400 fill-yellow-400"
                            />
                          ))}
                        </div>
                        <p className="text-lg text-gray-800 font-['Kalam'] leading-normal">
                          "No-frills card with great rates."
                        </p>
                      </div>
                    </div>

                    {/* Mobile view - static reviews instead of carousel with hooks */}
                    <div className="md:hidden text-center relative px-4">
                      <div className="flex justify-center mb-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className="w-4 h-4 text-yellow-400 fill-yellow-400"
                          />
                        ))}
                      </div>
                      <div className="min-h-[3rem] flex items-center justify-center">
                        <p className="text-lg text-gray-800 font-['Kalam'] leading-normal">
                          "Convenient way to off-ramp."
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Hero Content */}
                  <div className="text-center max-w-4xl mx-auto">
                    <h1 className="text-5xl sm:text-7xl font-serif font-black italic mb-4 leading-none sm:leading-tight tracking-tight">
                      <span className="bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent">
                        Spend Crypto Anywhere
                      </span>
                    </h1>
                    <p className="font-sans text-l text-gray-600 mb-6 max-w-2xl mx-auto">
                      Spend crypto at 70+ million merchants worldwide across 160
                      countries with competitive fees and transparent pricing.
                      Deposit easily from Solana, Arbitrum, Polygon and Hyperliquid.
                    </p>

                    <div className="mb-2 flex justify-center mt-12 sm:mt-0">
                      <img
                        src={basedCardImage}
                        width={1920}
                        height={1080}
                        style={{
                          transform: "scale(0.45)",
                          margin: "-100px 0",
                          maxHeight: "600px",
                        }}
                        alt="Based App Visa Card"
                        className="max-w-full h-auto shadow-xl rounded-2xl object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Custom Card Selection Section with white background */}
              <section className="py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    {/* Left side - Image */}
                    <div className="relative">
                      <div className="rounded-2xl overflow-hidden shadow-xl transform hover:scale-[1.02] transition-transform duration-300">
                        <img
                          src="/assets/main-spend-1.jpg"
                          width={1280}
                          height={720}
                          alt="BasedApp Card"
                          loading="lazy"
                          className="w-full h-auto"
                        />
                      </div>
                    </div>

                    {/* Right side - Text content */}
                    <div className="space-y-6">
                      <h2 className="font-sans font-semibold text-3xl sm:text-4xl  tracking-tight">
                        Skip the hassle of withdrawing crypto to your bank
                        account
                      </h2>
                      <p className="text-xl text-gray-600 leading-normal max-w-lg font-sans">
                        Deposit USDC from Solana, Arbitrum, Polygon and Hyperliquid. Spend
                        it like cash. Pay for your daily coffee in USDC.
                      </p>
                      <p className="text-xl text-gray-600 leading-normal max-w-lg font-sans">
                        We don't charge a "top-up" fee or "crypto-to-fiat"
                        conversion fee.
                      </p>
                      <div className="pt-4">
                        <a
                          href="https://help.basedapp.io/en/articles/7991297-what-are-the-fees-for-the-card"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 font-medium transition-colors underline">
                          See our Fees
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Easy Top-Up Section */}
              <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    {/* Left side - Text Content */}
                    <div className="space-y-6">
                      <h2 className="font-sans text-3xl sm:text-4xl font-semibold tracking-tight">
                        Not just for stables. <em>Crypto too.</em>
                      </h2>
                      <p className="font-sans text-xl text-gray-600 leading-normal max-w-lg">
                        Keep crypto in your account and spend whenever you need
                        to. You can keep SOL in your wallet and spend against it
                        anytime. Our auto-convert feature will convert your
                        crypto to USDC whenever you spend.
                      </p>
                    </div>

                    {/* Right side - Image */}
                    <div className="relative">
                      <div className="rounded-2xl overflow-hidden shadow-xl transform hover:scale-[1.02] transition-transform duration-300">
                        <img
                          src="/assets/main-photo-2.jpg"
                          width={1280}
                          height={720}
                          alt="BasedApp Card"
                          loading="lazy"
                          className="w-full h-auto"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Use Everywhere Section */}
              <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    {/* Left side - Image */}
                    <div className="relative">
                      <div className="rounded-2xl overflow-hidden shadow-xl transform hover:scale-[1.02] transition-transform duration-300">
                        <img
                          src="/assets/main-photo-3.jpg"
                          width={1280}
                          height={720}
                          alt="BasedApp Card"
                          loading="lazy"
                          className="w-full h-auto"
                        />
                      </div>
                    </div>

                    {/* Right side - Text Content */}
                    <div className="space-y-6">
                      <h2 className="font-sans font-bold text-3xl sm:text-4xl font-semibold tracking-tight">
                        Use it everywhere
                      </h2>
                      <p className="text-xl text-gray-600 leading-normal max-w-lg font-sans">
                        Your card works in most places Visa® is accepted and you
                        can add it to Apple Pay or Google Pay. Need a physical
                        card? Order one and we will send it to you. (Charges
                        Apply)
                      </p>
                      <div className="pt-4">
                        <div className="flex gap-4">
                          <img
                            src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b0/Apple_Pay_logo.svg/512px-Apple_Pay_logo.svg.png"
                            alt="Apple Pay"
                            className="h-8"
                          />
                          <img
                            src="https://developers.google.com/static/pay/api/images/brand-guidelines/google-pay-mark.png"
                            alt="Google Pay"
                            className="h-8"
                          />
                        </div>
                      </div>
                      <span className="block mt-2 text-xs text-gray-500">
                        The BasedApp Visa Card is issued in Singapore and may
                        not be accepted by some countries or merchants.
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Choose Your Plan Section */}
              <section className="py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-12">
                    Choose your plan
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                    {/* Standard Card */}
                    <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6 flex flex-col">
                      <div>
                        <h3 className="text-2xl font-semibold mb-2">
                          Standard
                        </h3>
                        <div>
                          <p className="text-3xl font-bold">
                            $30
                            <span className="text-lg font-normal text-gray-600">
                              /year
                            </span>
                          </p>
                          <p className="text-sm text-green-600 font-medium mt-1">
                            Free for Singapore users
                          </p>
                        </div>
                      </div>
                      <ul className="space-y-3 flex-grow">
                        <li className="flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>Use on Apple Pay and Google Pay</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>
                            No topup fee for USDT, USDC and XSGD (SGD)
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>Physical card available (+$70 USD)</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>1.5% foreign currency markup*</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>$3,000 USD daily spending limit</span>
                        </li>
                      </ul>
                      <div className="mt-6 space-y-4">
                        <p className="text-sm text-gray-600">
                          Available in Singapore, Malaysia and Hong Kong
                        </p>
                        <a
                          href="https://app.basedapp.io"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() =>
                            trackEvent("CTA", "Click", "Get Standard")
                          }
                          className="block w-full text-center px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-all duration-300">
                          Get Standard
                        </a>
                      </div>
                    </div>

                    {/* Gold Card */}
                    <div className="bg-gradient-to-br from-[#94814C] to-[#C7B276] rounded-2xl shadow-lg p-8 space-y-6 text-white flex flex-col">
                      <div>
                        <h3 className="text-2xl font-semibold mb-2">Gold</h3>
                        <p className="text-3xl font-bold">
                          $1,499
                          <span className="text-lg font-normal text-white/80">
                            /year
                          </span>
                        </p>
                      </div>
                      <ul className="space-y-3 flex-grow">
                        <li className="flex items-start">
                          <span className="text-white mr-2">✓</span>
                          <span>Use on Apple Pay and Google Pay</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-white mr-2">✓</span>
                          <span>
                            No topup fee for USDT, USDC and XSGD (SGD)
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-white mr-2">✓</span>
                          <span>50% more points earned on all spending</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-white mr-2">✓</span>
                          <span>Up to $30,000 USD daily spending limit</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-white mr-2">✓</span>
                          <span>Airport lounge access</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-white mr-2">✓</span>
                          <span>
                            Entertainment & conference rebates worth $700+
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-white mr-2">✓</span>
                          <span>Stainless Steel Mirror finish Gold Card</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-white mr-2">✓</span>
                          <span>0% foreign currency markup*</span>
                        </li>
                      </ul>
                      <div className="mt-6 space-y-4">
                        <p className="text-sm text-white/90">
                          Available worldwide except{" "}
                          <a
                            href="https://help.basedapp.io/en/articles/9614432-basedapp-country-availability"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-white/80 transition-colors">
                            unsupported countries or regions
                          </a>
                        </p>
                        <a
                          href="https://app.basedapp.io"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent("CTA", "Click", "Get Gold")}
                          className="block w-full text-center px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-gray-100 transition-all duration-300">
                          Get Gold
                        </a>
                        <Link
                          to="/gold"
                          onClick={() => {
                            trackEvent(
                              "Navigation",
                              "Click",
                              "Learn More Gold"
                            );
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className="block w-full text-center px-8 py-4 bg-transparent border border-white/50 text-white rounded-full font-medium hover:bg-white/10 transition-all duration-300">
                          Learn More
                        </Link>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 text-center mt-8">
                    *Markup from Visa's exchange rate
                  </p>
                </div>
              </section>

              {/* Security Focused Section */}
              <section className="py-16 bg-black text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    {/* Text Content */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <p className="text-gray-400 text-sm font-medium">
                          Security and Compliance
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
                          Your money is in a safe space
                        </h2>
                      </div>
                      <p className="text-gray-300 text-lg leading-normal max-w-xl">
                        BasedApp is built for security. We use industry-leading
                        technology trusted by Major Financial Institutions to
                        keep your funds safe.
                      </p>
                      <Link
                        to="/security"
                        onClick={() => {
                          trackEvent("Navigation", "Click", "Learn Security");
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="inline-flex items-center px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-opacity-90 transition-all duration-300">
                        Learn how we keep your money safe
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </div>

                    {/* 3D Lock Image */}
                    <div className="relative h-[400px] w-full rounded-2xl overflow-hidden transform hover:scale-[1.02] transition-transform bg-gradient-to-br from-black/60 to-gray-900/30 flex items-center justify-center">
                      <div className="lock-container">
                        <img
                          src={lockSvg}
                          width={300}
                          height={300}
                          alt="Secure Lock"
                          className="lock-image"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-tr from-black/40 to-transparent pointer-events-none" />
                    </div>
                  </div>
                </div>
              </section>

              {/* Go Green Section */}
              <section className="py-16 bg-[#FCF8F5] text-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="grid grid-cols-1 md:grid-cols-[22%_56%_22%] gap-8 items-center">
                    {/* Left column - Image 1 */}
                    <div className="hidden md:flex justify-center md:row-span-2">
                      <img
                        src="/assets/based-green-phone-2.png"
                        width={108}
                        height={192}
                        alt="BasedApp Green Trees Illustration"
                        className="max-h-48 w-auto object-contain"
                      />
                    </div>
                    {/* Center column - Text Content */}
                    <div className="space-y-6 text-center">
                      <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
                        Skip the plastic. Go Green with BasedApp. 🌱
                      </h2>
                      <p className="text-lg text-gray-600 font-sans leading-normal">
                        We do not issue physical Visa cards by default. When you
                        order a card, you'll get instant access to a digital
                        Visa card that you can add to Apple Pay or Google Pay
                        immediately. <br /> <br />
                        And we think it's better this way since{" "}
                        <Link
                          to="/blog/2025-04-15-digital-payments-safer"
                          onClick={() =>
                            trackEvent(
                              "Navigation",
                              "Click",
                              "Digital Payments Blog"
                            )
                          }
                          className="underline text-blue-600 hover:text-blue-800 transition-colors">
                          Apple Pay and Google Pay are more secure than physical
                          cards
                        </Link>
                        , gets to you faster and are supported nearly
                        everywhere.
                        <br />
                        <br />
                        If you really need a physical card, we charge $70 USD to
                        ship it anywhere in the world via DHL. For Gold Card
                        members, the physical card is included in the price.
                        <br />
                        <br />
                        Order a physical Visa card only if you truly need it —
                        and join us in reducing waste, one card at a time.
                      </p>
                      <div className="mt-6 hidden md:flex justify-center">
                        <img
                          src="/assets/horizontal_green.jpg"
                          width={384}
                          height={192}
                          alt="BasedApp Environmental Impact"
                          className="w-full max-w-sm h-auto object-contain"
                        />
                      </div>
                    </div>

                    {/* Right column - Image 2 */}
                    <div className="flex justify-center md:row-span-2">
                      <img
                        src="/assets/based-green-card.png"
                        width={202}
                        height={128}
                        alt="Green Based Card Illustration"
                        className="max-h-32 w-auto object-contain transform rotate-[20deg]"
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </Layout>
        }
      />
      {/* <Route path="/affiliate" element={<Affiliate />} /> */}
      <Route path="/waitlist" element={<Waitlist />} />
      {/* <Route path="/security" element={<Security />} /> */}
      {/* <Route path="/gold" element={<Gold />} /> */}
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/card-terms" element={<CardTerms />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      {/* <Route path="/faq" element={<FAQ />} /> */}
      {/* <Route path="/cards" element={<Cards />} /> */}
      <Route path="/" element={<BlogIndex />} />
      <Route path="/:slug" element={<BlogPost />} />
      <Route
        path="*"
        element={
          <Layout>
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold mb-4">Page Not Found</h1>
                <p className="mb-6">
                  The page you're looking for doesn't exist.
                </p>
                <a
                  href="/"
                  className="bg-gradient-to-r from-primary to-secondary text-white px-8 py-4 rounded-full">
                  Go Home
                </a>
              </div>
            </div>
          </Layout>
        }
      />
    </Routes>
  );
}

export default App;
